import CartApi from '@apis/cart/CartApi';
import queries from '@apis/queries';
import useCart from '@hooks/cart/useCart';
import useToast from '@hooks/ui/useToast';
import { ErrorResponse } from '@models/error';
import { OrderLine } from '@models/order';
import { cartActions } from '@store/cart/cartSlice';
import useCalculatedCart from '@store/cart/hooks/useCalculatedCart';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

function useModifyCartItem(item: OrderLine) {
    const { shopId } = useCalculatedCart();
    const { cart } = useCart(shopId);
    const { shopItemId, cartItemId } = item;
    const toast = useToast();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { t } = useTranslation('cart');
    const { mutate } = useMutation({
        mutationFn: () => CartApi.deleteCartItem(shopId, cart!.cartId!, cartItemId!),
        onSuccess() {
            queryClient.invalidateQueries({ queryKey: queries.cart._def });
            dispatch(cartActions.removeItemByCartItemId(cartItemId!));
            toast.info(t('toast.itemDeleted'));
        },
        onError(e: AxiosError<ErrorResponse>) {
            console.error(e);
            toast.error(e.response?.data?.message || t('toast.error'));
        },
    });
    const handleDelete = () => {
        mutate();
    };

    const handleDecrease = () => {
        dispatch(cartActions.updateItemQuantity({
            cartItemId: cartItemId!, itemId: shopItemId, amount: -1,
        }));
    };

    const handleIncrease = () => {
        dispatch(cartActions.updateItemQuantity({
            cartItemId: cartItemId!, itemId: shopItemId, amount: 1,
        }));
    };

    return {
        handleIncrease, handleDecrease, handleDelete,
    };
}

export default useModifyCartItem;
